import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { ArrowLeft, Save, Trash2, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Agent } from "@/types/aiToolkit";
import { aiAgentsService, agentToInsertPayload } from "@/services/aiAgentsService";
import {
  AgentBuildForm,
  defaultAgentPartial,
  validateAgentBuild,
} from "@/components/agents/AgentBuildForm";

export default function AgentControlPanel() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [formData, setFormData] = useState<Partial<Agent>>(defaultAgentPartial);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const agentId = params.id;

  const { data: agent, isLoading: agentLoading } = useQuery<Agent | null>({
    queryKey: ["/api/ai-agents", agentId, "detail"],
    queryFn: () => (agentId ? aiAgentsService.getAgentById(agentId) : Promise.resolve(null)),
    enabled: !!agentId,
  });

  useEffect(() => {
    if (agent) {
      setFormData({
        ...defaultAgentPartial,
        ...agent,
        persona: agent.persona || defaultAgentPartial.persona,
        outputRequirements: agent.outputRequirements || defaultAgentPartial.outputRequirements,
        linkedSourceIds: agent.linkedSourceIds ?? [],
        trainingDocumentIds: agent.trainingDocumentIds ?? [],
      });
      setHasUnsavedChanges(false);
    }
  }, [agent]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!agentId) throw new Error("No agent ID");
      return aiAgentsService.updateAgent(agentId, agentToInsertPayload(formData));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ai-agents"] });
      setHasUnsavedChanges(false);
      toast({ title: "Agent saved", description: "Your changes have been saved." });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to save", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!agentId) throw new Error("No agent ID");
      return aiAgentsService.deleteAgent(agentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ai-agents"] });
      toast({ title: "Agent deleted" });
      setLocation("/ai-agents");
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete", description: error.message, variant: "destructive" });
    },
  });

  const validation = validateAgentBuild(formData);

  if (agentLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="p-6 max-w-2xl mx-auto text-center">
        <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="text-muted-foreground mb-4">Agent not found</p>
        <Button variant="outline" onClick={() => setLocation("/ai-agents")}>
          Back to Agents Hub
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <Button variant="outline" onClick={() => setLocation("/ai-agents")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex gap-2">
          <Button
            onClick={() => updateMutation.mutate()}
            disabled={!validation.valid || updateMutation.isPending || !hasUnsavedChanges}
            data-testid="button-save-agent"
          >
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <span>{agent.icon}</span>
          Edit {agent.name}
        </h1>
        <p className="text-muted-foreground mt-1">
          Configure lens, expert focus, output requirements, and training documents
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Agent Builder</CardTitle>
          <CardDescription>
            Same guided builder as Library — all fields sync to your agent profile in chat
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AgentBuildForm
            value={formData}
            onChange={(next) => {
              setFormData(next);
              setHasUnsavedChanges(true);
            }}
            showFolderPicker
            scrollHeight="min(60vh, 640px)"
          />
        </CardContent>
      </Card>

      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>Irreversible actions</CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" type="button" data-testid="button-delete-agent">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Agent
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Agent</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete &quot;{agent.name}&quot;? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteMutation.mutate()}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
